import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Facebook,
  Instagram,
  AlertCircle,
  X,
  Copy,
} from 'lucide-react';
import api from '../services/api';
import PresignedImage from '../components/PresignedImage';
import Toast from '../components/Toast/Toast';
import '../styles/artist-list.css';

const ArtistList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchArtists();
    }
  }, [user]);

  const fetchArtists = async () => {
    try {
      const response = await api.get('/api/artists/all');
      setArtists(response.data.artists);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArtists = artists
    .filter((artist) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        artist.artist_name?.toLowerCase().includes(searchLower) ||
        artist.artist_legal_name?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) =>
      (a.artist_name || '').localeCompare(b.artist_name || '', undefined, { sensitivity: 'base' })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const copyArtistId = async (event, artistId) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(artistId);
      setShowCopiedToast(true);
    } catch (error) {
      console.error('Failed to copy artist id:', error);
    }
  };

  const openArtistPage = (artist) => {
    navigate(`/artist/${artist.id}`);
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <p className="text-gray-500">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50" />
      </div>
    );
  }

  return (
    <div className="artist-list-page artist-list-page--transparent">
      <Toast
        show={showCopiedToast}
        type="success"
        message="Copied artist id to clipboard"
        onDismiss={() => setShowCopiedToast(false)}
      />
      <div className="artist-list-header relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 space-y-5 p-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-white">Artist Library</h1>
            <div className="text-sm text-white whitespace-nowrap">
              {filteredArtists.length} of {artists.length} artists
            </div>
          </div>

          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by artist name or legal name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 w-full border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500 text-sm py-2 ${searchTerm ? 'pr-10' : 'pr-4'}`}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-0.5"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="artist-list-scroll">
        {filteredArtists.length === 0 ? (
          <div className="artist-list-panel p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-400 mb-2">No artists found</h4>
            <p className="text-gray-600 text-sm">
              {searchTerm ? 'No artists found matching your search' : 'No artists found'}
            </p>
          </div>
        ) : (
          <div className="artist-list-panel">
            <div className="artist-list-header-row text-white">
              <span aria-hidden="true" />
              <span>Artist</span>
              <span>Legal Name</span>
              <span>Joined</span>
            </div>

            {filteredArtists.map((artist) => (
              <div key={artist.id} className="artist-list-item">
                <div
                  className="artist-list-row"
                  onClick={() => openArtistPage(artist)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openArtistPage(artist);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="artist-list-image-col">
                    {artist.image ? (
                      <div className="artist-list-image">
                        <PresignedImage
                          assetKey={artist.image}
                          assetType="artist"
                          alt={artist.artist_name || 'Artist'}
                        />
                      </div>
                    ) : (
                      <div className="artist-list-image artist-list-image--placeholder" aria-hidden="true">
                        <Users className="h-7 w-7" />
                      </div>
                    )}
                  </div>

                  <div className="artist-list-name-col">
                    <div className="artist-list-name-row">
                      <span className="artist-list-name">{artist.artist_name || '—'}</span>
                      <button
                        type="button"
                        className="artist-list-copy-id"
                        title="Copy Artist Id to clipboard"
                        aria-label="Copy Artist Id to clipboard"
                        onClick={(event) => copyArtistId(event, artist.id)}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    {(artist.artist_fb_url || artist.artist_ig_url) && (
                      <div className="artist-list-social">
                        {artist.artist_fb_url && (
                          <a
                            href={artist.artist_fb_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="artist-list-social-link artist-list-social-link--facebook"
                            title="Facebook"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}
                        {artist.artist_ig_url && (
                          <a
                            href={artist.artist_ig_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="artist-list-social-link artist-list-social-link--instagram"
                            title="Instagram"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="artist-list-legal">{artist.artist_legal_name || '—'}</div>
                  <div className="artist-list-date">{formatDate(artist.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistList;
